async function request(url: string, options?: RequestInit) {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(`${res.status}: ${data.error || 'Request failed'}`);
  }
  return res.json();
}

export const api = {
  bands: {
    list: () => request('/api/bands'),
    get: (id: number) => request(`/api/bands/${id}`),
    create: (data: { name: string; description?: string }) => request('/api/bands', { method: 'POST', body: JSON.stringify(data) }),
    getMembers: (id: number) => request(`/api/bands/${id}/members`),
    addMember: (id: number, data: { email: string; role?: string }) => request(`/api/bands/${id}/members`, { method: 'POST', body: JSON.stringify(data) }),
    updateMemberRole: (bandId: number, memberId: number, role: string) => request(`/api/bands/${bandId}/members/${memberId}`, { method: 'PUT', body: JSON.stringify({ role }) }),
    removeMember: (bandId: number, memberId: number) => request(`/api/bands/${bandId}/members/${memberId}`, { method: 'DELETE' }),
  },
  songs: {
    list: (bandId: number, params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/api/songs/band/${bandId}${qs}`);
    },
    get: (id: number) => request(`/api/songs/${id}`),
    create: (data: any) => request('/api/songs', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/api/songs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/api/songs/${id}`, { method: 'DELETE' }),
  },
  charts: {
    getBaseForSong: (songId: number) => request(`/api/charts/base/song/${songId}`),
    getBase: (id: number) => request(`/api/charts/base/${id}`),
    createBase: (data: any) => request('/api/charts/base', { method: 'POST', body: JSON.stringify(data) }),
    updateBase: (id: number, data: any, createVersion = false) => request(`/api/charts/base/${id}${createVersion ? '?createVersion=true' : ''}`, { method: 'PUT', body: JSON.stringify(data) }),
    getPersonalForUser: (bandId?: number) => {
      const qs = bandId ? `?bandId=${bandId}` : '';
      return request(`/api/charts/personal/user${qs}`);
    },
    getPersonalForBase: (baseChartId: number) => request(`/api/charts/personal/base/${baseChartId}`),
    getPersonalDetail: (id: number) => request(`/api/charts/personal/detail/${id}`),
    createPersonal: (baseChartId: number, fromSharedVariantId?: number) => request('/api/charts/personal', { method: 'POST', body: JSON.stringify({ baseChartId, fromSharedVariantId }) }),
    updatePersonal: (id: number, data: any, createVersion = false) => request(`/api/charts/personal/${id}${createVersion ? '?createVersion=true' : ''}`, { method: 'PUT', body: JSON.stringify(data) }),
    acknowledgeUpdate: (id: number) => request(`/api/charts/personal/${id}/acknowledge-update`, { method: 'POST' }),
    getVersions: (chartType: string, chartId: number) => request(`/api/charts/versions/${chartType}/${chartId}`),
    restoreVersion: (chartType: string, chartId: number, versionId: number) => request(`/api/charts/versions/${chartType}/${chartId}/restore/${versionId}`, { method: 'POST' }),
    sharePersonal: (variantId: number, data: { bandId?: number; userIds?: string[] }) => request(`/api/charts/personal/${variantId}/share`, { method: 'POST', body: JSON.stringify(data) }),
    getPersonalShares: (variantId: number) => request(`/api/charts/personal/${variantId}/shares`),
    removePersonalShare: (variantId: number, shareId: number) => request(`/api/charts/personal/${variantId}/shares/${shareId}`, { method: 'DELETE' }),
    getSharedView: (variantId: number) => request(`/api/charts/personal/shared-view/${variantId}`),
    getSharedForSong: (songId: number) => request(`/api/charts/shared/song/${songId}`),
    getBaseEditors: (baseChartId: number) => request(`/api/charts/base/${baseChartId}/editors`),
    addBaseEditor: (baseChartId: number, userId: string) => request(`/api/charts/base/${baseChartId}/editors`, { method: 'POST', body: JSON.stringify({ userId }) }),
    removeBaseEditor: (baseChartId: number, editorUserId: string) => request(`/api/charts/base/${baseChartId}/editors/${editorUserId}`, { method: 'DELETE' }),
    transferBaseOwnership: (baseChartId: number, newOwnerUserId: string) => request(`/api/charts/base/${baseChartId}/transfer-ownership`, { method: 'POST', body: JSON.stringify({ newOwnerUserId }) }),
  },
  setlists: {
    list: (bandId: number) => request(`/api/setlists/band/${bandId}`),
    get: (id: number) => request(`/api/setlists/${id}`),
    create: (data: any) => request('/api/setlists', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/api/setlists/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/api/setlists/${id}`, { method: 'DELETE' }),
    addItem: (setlistId: number, songId: number) => request(`/api/setlists/${setlistId}/items`, { method: 'POST', body: JSON.stringify({ songId }) }),
    removeItem: (setlistId: number, itemId: number) => request(`/api/setlists/${setlistId}/items/${itemId}`, { method: 'DELETE' }),
    reorder: (setlistId: number, itemIds: number[]) => request(`/api/setlists/${setlistId}/reorder`, { method: 'PUT', body: JSON.stringify({ itemIds }) }),
    duplicate: (id: number) => request(`/api/setlists/${id}/duplicate`, { method: 'POST' }),
  },
  rehearsalNotes: {
    listForBand: (bandId: number) => request(`/api/rehearsal-notes/band/${bandId}`),
    get: (id: number) => request(`/api/rehearsal-notes/${id}`),
    create: (data: { bandId: number; title: string; visibility: string; selectedUserIds?: string[] }) =>
      request('/api/rehearsal-notes', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: { title?: string; visibility?: string; selectedUserIds?: string[] }) =>
      request(`/api/rehearsal-notes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/api/rehearsal-notes/${id}`, { method: 'DELETE' }),
    addEntry: (noteId: number, content: string) =>
      request(`/api/rehearsal-notes/${noteId}/entries`, { method: 'POST', body: JSON.stringify({ content }) }),
    updateEntry: (noteId: number, entryId: number, content: string) =>
      request(`/api/rehearsal-notes/${noteId}/entries/${entryId}`, { method: 'PUT', body: JSON.stringify({ content }) }),
    deleteEntry: (noteId: number, entryId: number) =>
      request(`/api/rehearsal-notes/${noteId}/entries/${entryId}`, { method: 'DELETE' }),
  },
  images: {
    upload: async (file: File, chartType: string, chartId: number) => {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('chartType', chartType);
      formData.append('chartId', String(chartId));
      const res = await fetch('/api/images/upload', { method: 'POST', body: formData, credentials: 'include' });
      if (!res.ok) throw new Error('Upload failed');
      return res.json();
    },
  },
};
